import { IconButton, List, ListItem, ListItemText } from "@mui/material";
import arrayMutators from "final-form-arrays";
import React, { useEffect, useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import RadioButton from "src/components/Form/RadioButtonV2/RadioButton";
import SingleSelectField from "src/components/Form/SingleSelectFieldV2/SingleSelectField";
import TextField from "src/components/Form/TextFieldV2/TextFieldV2";
import FormModal from "src/components/FormModalV2/FormModal";
import FilterContainer from "src/components/TableV2/FilterContainer";
import DisTable from "src/components/TableV2/Table";
import useAutosave from "src/hooks/useAutosave"; // Import the custom hook
import { IApplicationAgent } from "src/store/models/application.model";
import { IRootState } from "src/store/reducers";
import {
  agentPickerDismiss,
  agentPickerSelect,
  appBuilderRemoveBroker,
  fetchApplicationAgents,
  saveAgents,
  searchBrokerByText,
  transformAgent,
} from "src/store/reducers/applicationAgents";
import { appBuilderShouldSave } from "src/store/reducers/applicationBuilder";
import { addDefaultOption } from "src/util/utils.defaultValues";
import * as S from "../../../../constants/StringConstants";
import { TabProps } from "../../pages";
import { validateAgents } from "../../validator";

const columns = removeItem => [
  {
    key: "primary",
    title: S.APP_AGENTS_COL_PRIMARY,
    style: { minWidth: "5rem" },
    render: (item, _col, _index, name) => {
      return <RadioButton name={`primaryAgent`} value={item.agentId} label={""} />;
    },
  },
  { key: "agentFullName", title: S.APP_AGENTS_COL_NAME, style: { minWidth: "5rem" } },
  {
    key: "agentPercentage",
    title: S.APP_AGENTS_COL_PERCENTAGE,
    style: { width: "20rem" },
    render: (_item, _col, _index, name) => (
      <div style={{ width: "10rem", display: "flex", alignItems: "center" }}>
        <TextField name={`${name}.agentPercentage`} value="" bare />
      </div>
    ),
  },
  {
    key: "affiliateId",
    title: S.APP_AGENTS_COL_AFFILIATE,
    style: { minWidth: "5rem" },
    render: (item, _col, index, name) => (
      <div style={{ width: "10rem", display: "flex", alignItems: "center" }}>
        <SingleSelectField
          name={`${name}.affiliateId`}
          bare
          options={
            item.selectAgentAffiliates
              ? addDefaultOption(
                  S.BRO_AFFILIATE_NONE,
                  item.selectAgentAffiliates?.map(x => ({ key: x.affiliateName, value: x.affiliateId })),
                )
              : []
          }
        />
      </div>
    ),
  },
  { key: "agentCompanyName", title: S.APP_AGENTS_COL_COMPANY, style: { minWidth: "5rem" } },
  { key: "agentBranchName", title: S.APP_AGENTS_COL_BRANCH, style: { minWidth: "5rem" } },
  {
    key: "actions",
    title: "",
    style: { minWidth: "5rem" },
    render: (item, _col, index) => (
      <button className="button-v2 button-v2__secondary small" onClick={() => removeItem(item, index)}>
        {S.APP_AGENTS_DELETE_LABEL}
      </button>
    ),
  },
];

export const AppAgents = (props: TabProps) => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const { agents, agentsLoaded, agentsPickerSelected, agentsPickerList, agentsNotFound } = useSelector(
    (state: IRootState) => state.applicationAgents,
  );

  const [formRef, setFormRef] = useState(null);

  const performSave = async newAppId => {
    dispatch(appBuilderShouldSave(false));
    if (!formRef) {
      return;
    }
    const state = formRef.getState();
    const currentValues = state.values.rows as IApplicationAgent[];
    const removedAgents = agents.filter(agent => agent.agentRemovedByUser);
    await dispatch(saveAgents(parseInt(newAppId || id), state.values.primaryAgent, currentValues, removedAgents));
    await dispatch(fetchApplicationAgents(newAppId || id));
    props.onDirty(false);
  };

  const AutoSave = useAutosave(props, performSave);

  useEffect(() => {
    if (id !== "new") {
      dispatch(fetchApplicationAgents(id));
    }
  }, [id]);

  // new item?
  useEffect(() => {
    const state = formRef?.getState().values;
    if (agentsPickerSelected) {
      const isFirstAgent = !state.rows.length;
      const transformedAgent = transformAgent(agentsPickerSelected, isFirstAgent);
      formRef.mutators.push("rows", transformedAgent);
      if (isFirstAgent) {
        formRef.mutators.setPrimary(transformedAgent.agentId);
      }
      dispatch(agentPickerSelect(null));
    }
  }, [agentsPickerSelected]);

  useEffect(() => {
    if (agentsNotFound) {
      alert(S.APP_AGENTS_NOT_FOUND);
      dispatch(agentPickerDismiss());
    }
  }, [agentsNotFound]);

  const handleSearch = values => {
    dispatch(searchBrokerByText(values.agentSearch));
  };

  const handleRemove = (item, index) => {
    formRef.mutators.remove("rows", index);
    dispatch(appBuilderRemoveBroker(item));
  };

  const agentData = useMemo(() => {
    const filteredAgents = agents?.filter(agent => !agent.agentRemovedByUser);
    return {
      primaryAgent: filteredAgents.find(agent => agent.primary)?.agentId,
      rows: filteredAgents,
    };
  }, [agentsLoaded]);

  return (
    <div>
      <FormModal
        open={agentsPickerList.length > 0}
        saveLabel={null}
        onCancel={() => dispatch(agentPickerDismiss())}
        values={{}}
        onSubmit={() => {}}>
        <List dense={true} sx={{ maxHeight: "400px", overflowY: "auto" }}>
          {agentsPickerList.map(agent => (
            <>
              <ListItem
                key={agent.agentId}
                secondaryAction={
                  <IconButton edge="end" aria-label="select">
                    <button
                      className="button-v2 tiny primary"
                      onClick={() => {
                        dispatch(agentPickerSelect(agent));
                        dispatch(agentPickerDismiss());
                      }}>
                      {S.APP_AGENTS_USE_LABEL}
                    </button>
                  </IconButton>
                }>
                <ListItemText
                  primary={agent.brokerName}
                  secondary={agent.companyName || "-"}
                  primaryTypographyProps={{ fontSize: "14px" }}
                />
              </ListItem>
              <hr />
            </>
          ))}
        </List>
      </FormModal>
      <Form
        initialValues={agentData}
        validateOnBlur
        onSubmit={() => alert("submit")}
        validate={validateAgents}
        mutators={{
          ...arrayMutators,
          setPrimary: (args, state, utils) => {
            utils.changeValue(state, "primaryAgent", () => args[0]);
          },
        }}
        render={({ form, dirty, valid, errors }) => {
          if (!formRef) {
            setFormRef(form);
          }
          if (dirty) {
            props.onDirty(true);
          }
          if (props.onValid) {
            props.onValid(valid, errors);
          }
          return (
            <DisTable<IApplicationAgent>
              columns={columns(handleRemove)}
              useFormBody={true}
              isPaginated={false}
              onUpdate={async () => {
                return Promise.resolve({
                  total: 0,
                  data: [] as IApplicationAgent[],
                });
              }}>
              <Form
                initialValues={{}}
                onSubmit={handleSearch}
                render={({ handleSubmit, valid, values }) => (
                  <FilterContainer
                    left={
                      <>
                        <label>
                          <strong>{S.APP_AGENTS_AGENT_NAME_LABEL}:</strong>
                        </label>
                        <div className="applications-custom-textfilter">
                          <TextField
                            placeholder="Search"
                            name="agentSearch"
                            bare={true}
                            inputProps={{
                              onKeyPress: e => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleSubmit();
                                }
                              },
                            }}
                          />
                        </div>
                        <button className="button-v2 gray small" onClick={handleSubmit}>
                          {S.APP_AGENTS_SEARCH}
                        </button>
                      </>
                    }
                  />
                )}
              />
            </DisTable>
          );
        }}
      />
      <div className="mb-5" />
      <AutoSave />
    </div>
  );
};
